/**
 * The sign-in screen. Shared, because both apps need the same one.
 *
 * The error handling is the substance here. The API distinguishes three
 * failures and each deserves a different message:
 *
 * - **wrong credentials** — one message for a bad password and an unknown
 *   login, because any difference between them confirms an account to whoever
 *   is guessing;
 * - **locked out** — says how long, because "locked" with no deadline leaves
 *   somebody retrying or calling support;
 * - **wrong app** — says so plainly. This is not a guess, it is somebody who
 *   was mailed the wrong address, and telling them is the difference between a
 *   door they can find and a product that looks broken.
 *
 * What a login looks like differs by app — a username, email or sign-in mobile
 * for staff, a login email or mobile number for a client — so the app names the
 * field. Either kind can also sign in with a one-time code, which the app turns
 * on; the server decides by the door whose code it is, and a code asked for at
 * one door never signs anybody in at the other.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, MessageSquareText, User } from 'lucide-react';

import { appConfig } from '../../config/app-config';
import { ApiError } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { returnTo } from '../../auth/return-to';
import { useSession, type CodeRequestResult } from '../../auth/session';
import { Button } from '../ui/button';
import { Input, PasswordInput } from '../ui/input';
import { Alert } from '../ui/surfaces';
import { ProductAttribution } from '../branding/product-attribution';
import { BrandMark } from './brand-mark';

export interface SignInPageProps {
  /** Where to land after signing in. */
  redirectTo?: string;
  /** A line under the product name — the organization, or a tagline. */
  subtitle?: ReactNode;
  /** Shown at the foot of the card. */
  footer?: ReactNode;
  /** What the login field is called in this app. */
  loginLabel?: string;
  loginPlaceholder?: string;
  /**
   * Offer a one-time code as well as a password. A code goes to the mobile or
   * address typed — or, for a staff username, to the account's address.
   */
  offersCodes?: boolean;
}

type Mode = 'password' | 'code';

// WhatsApp is promised with its fallback, because it is only the first try: a
// number that is not on WhatsApp gets the code by text a moment later.
const ROUTE_NOTE: Record<CodeRequestResult['route'], string> = {
  whatsapp: " on WhatsApp — or by text, if this number isn't on WhatsApp",
  sms: ' by text',
  email: ' by email',
};

function asApiError(caught: unknown): ApiError {
  return caught instanceof ApiError
    ? caught
    : new ApiError({
        status: 0,
        code: 'NETWORK',
        // A network failure is not a rejected credential, and saying so stops
        // somebody retyping a password that was always correct.
        message: 'Could not reach the server. Check your connection and try again.',
      });
}

export function SignInPage({
  redirectTo = '/',
  subtitle,
  footer,
  loginLabel = 'Username, email or mobile',
  loginPlaceholder = 'admin, you@example.com or 98480 22338',
  offersCodes = false,
}: SignInPageProps) {
  const signIn = useSession((state) => state.signIn);
  const requestCode = useSession((state) => state.requestCode);
  const signInWithCode = useSession((state) => state.signInWithCode);
  const navigate = useNavigate();
  // Back where RequireAuth found them, when it was a page of this app.
  const target = returnTo(useLocation().state, redirectTo);

  const [mode, setMode] = useState<Mode>('password');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<CodeRequestResult | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // The resend countdown. The server enforces the wait; this only saves somebody
  // pressing a button that will be refused.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setSent(null);
    setCode('');
    setError(null);
    setResendIn(0);
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await requestCode(login);
      setSent(result);
      setResendIn(result.resend_after_seconds);
      // Development hands the code back; filling it in is what a developer wants.
      setCode(result.debug_code ?? '');
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'code' && !sent) {
      await sendCode();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'code') await signInWithCode(login, code);
      else await signIn(login, password);
      navigate(target, { replace: true });
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    // A column the height of the window: the form centred in the space above,
    // and the attribution at the bottom edge, where provenance belongs — close
    // under the card it read as part of signing in.
    <div className="flex min-h-dvh flex-col bg-background px-4">
      <div className="flex flex-1 items-center justify-center py-10">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <BrandMark size="lg" className="mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-heading">{appConfig().appName}</h1>
            {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
          </div>

          <form
            onSubmit={submit}
            className="space-y-4 rounded-card border border-line bg-surface p-5 shadow-card"
          >
            {offersCodes ? (
              <div className="grid grid-cols-2 gap-1 rounded-control bg-surface-2 p-1" role="tablist">
                {(
                  [
                    ['password', 'Password'],
                    ['code', 'One-time code'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={mode === value}
                    disabled={busy}
                    onClick={() => switchMode(value)}
                    className={cn(
                      'rounded-control px-2 py-1.5 text-xs font-medium transition-colors',
                      mode === value ? 'bg-surface text-heading shadow-card' : 'text-muted hover:text-body',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            {error ? (
              <Alert
                tone={error.code === 'WRONG_APP' ? 'warning' : 'danger'}
                title={
                  error.code === 'ACCOUNT_LOCKED'
                    ? 'Too many attempts'
                    : error.code === 'WRONG_APP'
                      ? 'Wrong application'
                      : 'Could not sign in'
                }
              >
                {/* The API writes a message a person can act on, so it is shown
                    verbatim rather than replaced with something friendlier that
                    discards the specifics. */}
                {error.message}
              </Alert>
            ) : null}

            {/* `text`, not `email`: an email input refuses `admin` and a mobile
                number in the browser, before the request is ever sent. */}
            <Input
              label={loginLabel}
              type="text"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              readOnly={mode === 'code' && sent !== null}
              required
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              prefix={<User className="size-3.5" />}
              placeholder={loginPlaceholder}
            />

            {mode === 'password' ? (
              <PasswordInput
                label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
                prefix={<KeyRound className="size-3.5" />}
              />
            ) : sent ? (
              <>
                <p className="text-xs leading-relaxed text-muted">
                  If {login} signs in here, a code is on its way
                  {ROUTE_NOTE[sent.route]}. It works once, for{' '}
                  {Math.round(sent.expires_in_seconds / 60)} minutes.
                </p>
                <Input
                  label="Code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={12}
                  prefix={<MessageSquareText className="size-3.5" />}
                />
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    className="text-muted hover:text-body"
                    onClick={() => switchMode('code')}
                  >
                    Use a different login
                  </button>
                  <button
                    type="button"
                    className="text-accent hover:underline disabled:text-faint disabled:no-underline"
                    disabled={busy || resendIn > 0}
                    onClick={() => void sendCode()}
                  >
                    {resendIn > 0
                      ? `Send again in ${resendIn}s`
                      : sent.route === 'whatsapp'
                        ? 'Send it by text instead'
                        : 'Send a new code'}
                  </button>
                </div>
              </>
            ) : null}

            <Button type="submit" loading={busy} className="w-full justify-center">
              {mode === 'code' && !sent ? 'Send code' : 'Sign in'}
            </Button>
          </form>

          {footer ? <div className="mt-4 text-center text-xs text-faint">{footer}</div> : null}
        </div>
      </div>

      <footer className="flex justify-center pb-6 pt-2">
        <ProductAttribution />
      </footer>
    </div>
  );
}
